import { Alert, Typography } from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'

import { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import TrackedExternalLink from 'components/common/TrackedExternalLink'
import { ServiceKubernetesTargetingSelection } from 'components/services/create/target-selection/kubernetes/ServiceKubernetesTargetingSelection'
import { ServiceTagTargetingSelection } from 'components/services/create/target-selection/ServiceTagTargetingSelection'
import useAgent from 'hooks/use-agent'
import useAuth from 'hooks/use-auth'
import {
  activeInfraClientSelector,
  activeK8sClientSelector,
  useInfraClientsStore,
  useK8sClientsStore
} from 'hooks/use-clients'
import { Client } from 'types/api/attacks/client'
import { K8sObject } from 'types/api/kubernetes/kubernetes'
import { ServiceReadinessAgents } from 'types/api/services/service-creation-readiness'
import { ServiceFormValues } from 'types/forms'
import { pluralize } from 'utils/strings'

function computePodsFromK8sObject(
  k8sObj: K8sObject,
  k8sObjects: K8sObject[],
  accumulator: K8sObject[],
  level: number
) {
  if (!k8sObj) return

  if (k8sObj.kind === 'POD') {
    accumulator.push(k8sObj)
    return
  }
  if (level >= 3) {
    //we don't go deeper than 4 levels avoiding potential infinite loop
    return
  }

  k8sObjects
    .filter(
      (kObj) => kObj.ownerReferences && kObj.ownerReferences === k8sObj.uid
    )
    .forEach((kObj) => {
      computePodsFromK8sObject(kObj, k8sObjects, accumulator, level + 1)
    })
}

function getPods(k8sObjects: K8sObject[], selectedUIDs: string[]): string[] {
  const objArray = selectedUIDs
    .flatMap((uid) => {
      const accumulator = []
      computePodsFromK8sObject(
        k8sObjects.find((k) => k.uid === uid),
        k8sObjects,
        accumulator,
        0
      )
      return accumulator
    })
    .map((o) => o.uid)

  return [...new Set(objArray)]
}

export const ServiceFingerprint: React.FC<{
  setDisableFutureSteps(boolean): void
}> = ({ setDisableFutureSteps }) => {
  const { getValues } = useFormContext<ServiceFormValues>()

  const {
    selectedClients: selectedFormClients,
    selectedObjects: selectedFormObjects,
    targetParams,
    type: serviceType
  } = getValues()

  const clients = useInfraClientsStore(activeInfraClientSelector)
  const k8sClients = useK8sClientsStore(activeK8sClientSelector)
  const { currentTeamId } = useAuth()

  const podUIDs = getPods(
    k8sClients.flatMap((k) => k.objects),
    Array.from(selectedFormObjects)
  )

  const { data: serviceReadinessAgents } = useAgent<ServiceReadinessAgents>(
    {
      url: `/services/service-creation-readiness/clients?teamId=${currentTeamId}`,
      method: 'POST',
      data: {
        podUIDs: podUIDs
      }
    },
    { clientIds: [] } as ServiceReadinessAgents,
    { skipRequest: !selectedFormObjects.size }
  )

  const selectedClients =
    selectedFormObjects.size > 0
      ? clients
          ?.map((c) => c as Client)
          .filter((c) =>
            serviceReadinessAgents?.clientIds?.includes(c.identifier)
          )
      : selectedFormClients

  const selectedClientsWithoutProcessCollection = selectedClients
    ?.filter((c) => !c.process_collection_enabled)
    .map((c) => c.identifier)

  const selectedClientsWithoutZoneTags = selectedClients
    ?.filter((c) => !c.tags?.zone)
    .map((c) => c.identifier)

  return (
    <>
      <Typography data-test-service-fingerprint-description variant="body1">
        Now make at least one selection to define the scope of what Gremlin will
        evaluate during the Reliability Tests.
      </Typography>
      {serviceType === ProvenanceTargetType.ProcessData && (
        <ServiceTagTargetingSelection
          clients={clients}
          setDisableFutureSteps={setDisableFutureSteps}
          type={serviceType}
        />
      )}
      {serviceType === ProvenanceTargetType.Container && (
        <ServiceTagTargetingSelection
          clients={clients}
          setDisableFutureSteps={setDisableFutureSteps}
          type={serviceType}
        />
      )}
      {serviceType === ProvenanceTargetType.Kubernetes && (
        <ServiceKubernetesTargetingSelection
          clients={k8sClients}
          setDisableFutureSteps={setDisableFutureSteps}
        />
      )}

      {selectedClientsWithoutProcessCollection?.length > 0 && (
        <Alert
          data-testid="agents-without-process-collection"
          severity="warning"
        >
          {pluralize('Agent', selectedClientsWithoutProcessCollection.length)}{' '}
          without{' '}
          <TrackedExternalLink
            href="https://www.gremlin.com/docs/getting-started/enabling-process-collection/"
            target="_blank"
          >
            process collection
          </TrackedExternalLink>{' '}
          enabled:{' '}
          <strong>{selectedClientsWithoutProcessCollection.join(', ')}</strong>
        </Alert>
      )}

      {selectedClientsWithoutZoneTags?.length > 0 && (
        <Alert data-testid="agents-without-zone-tags" severity="info">
          No zone tags were found for{' '}
          <strong>{selectedClientsWithoutZoneTags.join(', ')}</strong>{' '}
          {pluralize('agent', selectedClientsWithoutZoneTags.length)}. For zone
          tests, the Gremlin{' '}
          <TrackedExternalLink
            href="https://www.gremlin.com/docs/reliability-management/reliability-tests/#zone"
            target="_blank"
          >
            zone tag
          </TrackedExternalLink>{' '}
          is required.
        </Alert>
      )}
      {selectedClients.length === 0 &&
        Object.values(targetParams)?.find((p) => p?.size > 0) && (
          <Alert data-testid="no-agents-selected" severity="warning">
            No agents match the current tag selection
          </Alert>
        )}
    </>
  )
}
